import { Box, ConfirmationModal, Flex, Text } from '@modusign/design-system';
import { isFieldGroup, useFields, useSigners } from '@modusign/react-signing';
import { useUIState } from '@modusign/react-signing/setup';
import { Field, FieldNode } from '@modusign/react-signing/types';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useMount } from 'react-use';
import { DeepReadonly } from 'ts-essentials';
import { v4 } from 'uuid';

import FieldCannotBeModifiedAlertModal from '@app/components/Modal/FieldCannotBeModifiedAlertModal';
import RequesterFieldHasEmptyValueConfirmModal from '@app/components/Modal/RequesterFieldHasEmptyValueConfirmModal';
import { supportedFieldsContractLinkCreateFieldSetupContent } from '@app/constants/supportedFieldsPerEditor';
import MouseFieldPlaceholder from '@app/containers/MouseFieldPlaceholder';
import PDFSetupBusinessPage from '@app/containers/PDFConfigurableBusinessPages/PDFSetupBusinessPage';
import TemplateFieldFooter from '@app/containers/TemplateFieldFooter';
import DocumentSetupSecondStepBoards from '@app/features/apply-react-signing/components/FieldSetupStep/ParticipantFIeldStatusBoards';
import EditorFieldInputNextStepWarningModal from '@app/features/apply-react-signing/components/Modal/EditorFieldInputNextStepWarningModal';
import {
  confirmThatFieldCannotBeModified,
  getConfirmedThatFieldCannotBeModified,
} from '@app/features/contractLink/contractLinkCreate';
import { createDataLabel } from '@app/features/documents/utils/createDataLabel';
import { usePDF } from '@app/hooks/usePDF';
import useResponsiveValue from '@app/hooks/useResponsiveValue';
import { checkIsRequesterHasEmptyFieldValue } from '@app/libs/fields/checkIsRequesterHasEmptyFieldValue';
import { getSignersWithoutSignatureField } from '@app/libs/getSignersWithoutSignatureField';
import PDFDocumentContentLayout from '@app/libs/reactPdfJs/components/PDFDocumentContentLayout';
import PDFContainer from '@app/libs/reactPdfJs/containers/PDFContainer';
import { getPdfMaxPage } from '@app/libs/reactPdfJs/store/selectors';

function getPageInField(field: DeepReadonly<FieldNode>) {
  if (isFieldGroup(field) || !('position' in field)) {
    return field.pages[0];
  }
  return field.position.page;
}

interface ContractLinkCreateFieldSetupContentProps {
  onNextStep: () => void;
}

function ContractLinkCreateFieldSetupContent({
  onNextStep,
}: ContractLinkCreateFieldSetupContentProps) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { fields, changeFields } = useFields();

  const { signers, addSigner } = useSigners();
  const { ui, setInOrder } = useUIState();

  const maxPageNumberInFields = useMemo(
    () => fields.map((field) => getPageInField(field)).reduce((acc, val) => Math.max(acc, val), 0),
    [fields]
  );
  const isRequesterHasEmptyFieldValue = useMemo(
    () => checkIsRequesterHasEmptyFieldValue(fields),
    [fields]
  );

  const signersWithoutSignatureField = useMemo(
    () => getSignersWithoutSignatureField(signers, fields),
    [fields, signers]
  );

  const confirmedThatFieldCannotBeModified = useSelector(getConfirmedThatFieldCannotBeModified);
  const { pdf } = usePDF();

  const pdfMaxPage = useSelector(getPdfMaxPage);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [
    isRequesterFieldHasEmptyValueConfirmModalOpen,
    setIsRequesterFieldHasEmptyValueConfirmModalOpen,
  ] = useState(false);
  const [isFieldCannotBeModifiedAlertModalOpen, setIsFieldCannotBeModifiedAlertModalOpen] =
    useState(false);
  const [isNextStepWarningModalOpen, setIsNextStepWarningModalOpen] = useState(false);
  const isMobileBreakpoint = useResponsiveValue([true, false]);

  useEffect(() => {
    if (isMobileBreakpoint && !confirmedThatFieldCannotBeModified) {
      setIsFieldCannotBeModifiedAlertModalOpen(true);
      dispatch(confirmThatFieldCannotBeModified());
    }
  }, [confirmedThatFieldCannotBeModified, dispatch, isMobileBreakpoint]);

  useMount(() => {
    if (signers.length === 0) {
      const id = v4();
      addSigner({
        id,
        dataLabel: createDataLabel(),
        type: 'SIGNER',
        signingMethod: 'SECURE_LINK',
        index: 0,
        order: 1,
      });
    }
  });

  useEffect(() => {
    if (ui.isInOrder) {
      // (@Juyeong-Byeon) 링크서명의 경우 항상 1인이고, 어떤 템플릿이 와도 순서 없는 서명으로 처리됩니다.
      setInOrder(false);
    }
  }, [dispatch, ui.isInOrder, setInOrder]);

  useEffect(() => {
    if (maxPageNumberInFields > pdfMaxPage && pdfMaxPage > 0) {
      changeFields((fields) => {
        return fields.filter((field) => {
          return getPageInField(field) <= pdfMaxPage;
          /**
           * (@Juyeong-Byeon)
           * 아래 이슈에서 기술한대로 updater function 타입 개선 후 제거
           * https://github.com/modusign/react-signing/issues/107
           */
        }) as Field[];
      });
    }
  }, [changeFields, maxPageNumberInFields, pdfMaxPage]);

  function handleBackStep() {
    setIsConfirmOpen(true);
  }

  function handleNextStep() {
    if (isRequesterHasEmptyFieldValue || signersWithoutSignatureField.length > 0) {
      setIsNextStepWarningModalOpen(true);
    } else {
      onNextStep();
    }
  }

  return (
    <>
      <PDFDocumentContentLayout>
        <MouseFieldPlaceholder />
        <Flex data-testid="field-setup-content" flex="1" height="100%" width="100%">
          <Box
            backgroundColor="lightGray25"
            className="
            Card
            Card--theme-editor
            RequestSettingPosition__joiner-card-block-box"
            display={['none', 'block']}
            minWidth="unset"
          >
            <DocumentSetupSecondStepBoards
              isTemplateSetting={false}
              needAuthorityCheck={false}
              supportedFields={supportedFieldsContractLinkCreateFieldSetupContent}
            />
          </Box>
          {pdf && (
            <Box flex="1">
              <PDFContainer
                PDFBusinessPage={PDFSetupBusinessPage}
                disablePinchZoom
                src={pdf}
                thumbnailContainerClassName="hidden"
                useThumbnailViewer={false}
                viewerPT={220}
              />
            </Box>
          )}
        </Flex>
      </PDFDocumentContentLayout>
      <TemplateFieldFooter
        backButtonText="< 나가기"
        nextButtonText="다음 단계로 >"
        onBackStepClick={handleBackStep}
        onNextStepClick={handleNextStep}
      />
      <ConfirmationModal
        confirmButtonLabel="나가기"
        isOpen={isConfirmOpen}
        maxWidth="xxs"
        title="나가기"
        variant="warning"
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={() => {
          setIsConfirmOpen(false);
          history.goBack();
        }}
      >
        <Text as="p" textAlign="center">
          입력한 내용이 저장되지 않습니다.
          <br />
          정말 나가시겠습니까?
        </Text>
      </ConfirmationModal>
      {isRequesterFieldHasEmptyValueConfirmModalOpen && (
        <RequesterFieldHasEmptyValueConfirmModal
          isOpen={isRequesterFieldHasEmptyValueConfirmModalOpen}
          onClose={() => setIsRequesterFieldHasEmptyValueConfirmModalOpen(false)}
          onConfirm={() => {
            setIsRequesterFieldHasEmptyValueConfirmModalOpen(false);
            onNextStep();
          }}
        />
      )}
      {isFieldCannotBeModifiedAlertModalOpen && (
        <FieldCannotBeModifiedAlertModal
          isOpen={isFieldCannotBeModifiedAlertModalOpen}
          onConfirm={() => setIsFieldCannotBeModifiedAlertModalOpen(false)}
        />
      )}
      {isNextStepWarningModalOpen && (
        <EditorFieldInputNextStepWarningModal
          hasRequesterFieldEmptyValue={isRequesterHasEmptyFieldValue}
          signerTotalCount={signers.length}
          signersWithoutSignatureField={signersWithoutSignatureField}
          onCancel={() => setIsNextStepWarningModalOpen(false)}
          onConfirm={() => {
            setIsNextStepWarningModalOpen(false);
            onNextStep();
          }}
        />
      )}
    </>
  );
}

export default ContractLinkCreateFieldSetupContent;
